import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { persistLocale, readStoredLocale, translate } from "./resolve";
import type { Locale, TranslationKey, TranslationParams } from "./types";

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /**
   * Resolve a dot-notation translation key for the active locale.
   * Falls back to the key path itself when a translation is
   * missing, so a missing string is visible rather than blank.
   */
  t: (key: TranslationKey, params?: TranslationParams) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale());

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    persistLocale(next);
  }, []);

  const t = useCallback(
    (key: TranslationKey, params?: TranslationParams) =>
      translate(locale, key, params),
    [locale]
  );

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t]
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used within a LocaleProvider.");
  }
  return ctx;
}

/**
 * Convenience hook for components that only need the ``t``
 * resolver and not the locale-switching controls.
 */
export function useTranslation(): {
  t: LocaleContextValue["t"];
  locale: Locale;
} {
  const { t, locale } = useLocale();
  return { t, locale };
}
